import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useNotification } from '@/components/notifications';
import { QUALIFICATION_BOARD_QUERY_KEY } from '@/hooks/queries/use-qualification-board';
import {
  PromotionResult,
  QualificationStage,
  promoteAttempt,
} from '@/services/workflow/qualification';

interface UsePromoteAttemptOptions {
  onPromoted?: (result: PromotionResult) => void;
}

export const usePromoteAttempt = ({
  onPromoted,
}: UsePromoteAttemptOptions = {}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<
    PromotionResult,
    Error,
    { attemptId: string; toStage: QualificationStage; workflowName?: string }
  >({
    mutationFn: ({ attemptId, toStage }) => promoteAttempt(attemptId, toStage),
    onSuccess: (result, { workflowName }) => {
      queryClient.invalidateQueries({
        queryKey: [QUALIFICATION_BOARD_QUERY_KEY],
      });
      notification.success(
        `${workflowName ?? 'Workflow'} promoted to ${result.to_stage.toUpperCase()}`,
      );
      onPromoted?.(result);
    },
    onError: (error) => {
      // A 409 here is the gate refusing, which is information rather than a
      // fault: the other side has not signed yet.
      notification.error(error.message || 'Could not promote');
      Sentry.captureException(error, { extra: { context: 'promote-attempt' } });
    },
  });
};
